import type { QueryClient } from '@tanstack/react-query';
import { getGetCourseQueryKey } from '@/common/api/endpoints/courses/courses';
import {
  getGetModulesByCourseQueryKey,
  getGetModulesQueryKey,
} from '@/common/api/endpoints/modules/modules';
import { getGetTasksByCourseModuleQueryKey } from '@/common/api/endpoints/tasks/tasks';
import { useAppStateStore } from '@/common/context/AppStateStore';
import { useAutosaveRegistryStore } from '@/common/stores/AutosaveRegistry';
import { isUUID } from '@/common/types/ids';
import { useMcqAttemptStore } from '@/student/task/composer/MCQComposer/store/useMcqAttemptStore';
import { useVideoPlayerStore } from '@/student/task/components/VideoPlayer/store/videoPlayerStore';
import { useTaskSubmissionStore } from '@/student/task/stores/TaskSubmissionStore';

const MODULE_TASK_INCLUDES = { include: 'submissions,resources' } as const;

export type RefreshStudentModuleParams = {
  courseId: string;
  moduleId: string;
};

/**
 * Clears client-side module/task state and refetches server data after a version pin changes.
 * Used when a student accepts a module upgrade offer (new tasks, submissions, upgrade ribbon).
 */
export const resetStudentModuleClientState = (moduleId: string) => {
  if (isUUID(moduleId)) {
    useAppStateStore.getState().setSelectedTask(moduleId, null);
  }

  useTaskSubmissionStore.getState().reset();
  useMcqAttemptStore.getState().resetAttempt();
  useVideoPlayerStore.getState().resetState();
  useAutosaveRegistryStore.setState({ inFlightSaves: {} });
};

export const refreshStudentModuleAfterVersionChange = async (
  queryClient: QueryClient,
  { courseId, moduleId }: RefreshStudentModuleParams,
) => {
  resetStudentModuleClientState(moduleId);

  const tasksQueryKey = getGetTasksByCourseModuleQueryKey(
    courseId,
    moduleId,
    MODULE_TASK_INCLUDES,
  );

  await Promise.all([
    queryClient.invalidateQueries({ queryKey: getGetModulesQueryKey() }),
    queryClient.invalidateQueries({ queryKey: getGetModulesByCourseQueryKey(courseId) }),
    queryClient.invalidateQueries({ queryKey: getGetCourseQueryKey(courseId) }),
    queryClient.invalidateQueries({ queryKey: getGetTasksByCourseModuleQueryKey(courseId, moduleId) }),
    queryClient.invalidateQueries({ queryKey: tasksQueryKey }),
  ]);

  await Promise.all([
    queryClient.refetchQueries({ queryKey: getGetModulesQueryKey() }),
    queryClient.refetchQueries({ queryKey: tasksQueryKey }),
  ]);
};
